import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  X,
  GripVertical,
  Save,
  Trash2,
  AlertCircle,
  Loader2,
  Code,
  Filter,
  Group,
  ArrowUpDown,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronUp,
  PlayCircle,
  CheckCircle,
  XCircle,
  AlignLeft,
  AlignJustify,
} from 'lucide-react';
import { formatSql, SqlFormatMode } from '../../../../utils/sqlFormat';
import { ruleCompositionService, RuleSqlTestResult } from '../../../../services/ruleCompositionService';
import {
  RuleInGroupDto,
  RuleGroupForCompositionDto,
  SqlFilterDto,
  SqlGroupDto,
  SqlOrderDto,
  UpdateRuleInGroupRequest,
} from '../../../../types/ruleComposition';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface RuleGroupDetailModalProps {
  group: RuleGroupForCompositionDto;
  onClose: () => void;
  onRulesChanged: () => void;
}

// SQL Test Result Component with Preview Table
function SqlTestResultDisplay({
  isLoading,
  result,
  error
}: {
  isLoading: boolean;
  result: RuleSqlTestResult | null;
  error: string | null;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
        <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
        <span className="text-sm font-medium text-blue-700">{t('manager.modules.ruleComposition.sqlTest.testing', {}, 'Tester SQL...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
        <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
        <span className="text-sm text-red-700">{error}</span>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="space-y-3">
      {/* Row count summary */}
      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
        <span className="text-sm font-medium text-green-700">
          {result.affectedRowCount === 1
            ? t('manager.modules.ruleComposition.sqlTest.affectedRowsOne', { '0': result.affectedRowCount }, `${result.affectedRowCount} rad vil bli påvirket`)
            : t('manager.modules.ruleComposition.sqlTest.affectedRowsMany', { '0': result.affectedRowCount }, `${result.affectedRowCount} rader vil bli påvirket`)}
        </span>
      </div>

      {/* Converted SELECT query */}
      {result.convertedSelectQuery && (
        <div>
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
            {t('manager.modules.ruleComposition.sqlTest.convertedSql', {}, 'Konvertert SQL (SELECT)')}
          </div>
          <div className="p-3 bg-gray-900 rounded-lg text-xs font-mono text-blue-300 max-h-32 overflow-auto whitespace-pre-wrap">
            {result.convertedSelectQuery}
          </div>
        </div>
      )}

      {/* Preview table */}
      {result.previewRows && result.previewRows.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
            {t('manager.modules.ruleComposition.sqlTest.previewRows', {}, 'Forhåndsvisning av rader (maks 10)')}
          </div>
          <div className="border border-gray-200 rounded-lg overflow-auto max-h-64">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  {result.columnNames.map((col, idx) => (
                    <th
                      key={idx}
                      className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {result.previewRows.map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-gray-50">
                    {result.columnNames.map((col, colIdx) => (
                      <td
                        key={colIdx}
                        className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-xs truncate"
                        title={String(row[col] ?? '')}
                      >
                        {row[col] === null ? (
                          <span className="text-gray-400 italic">null</span>
                        ) : row[col] === undefined ? (
                          <span className="text-gray-400 italic">-</span>
                        ) : (
                          String(row[col])
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.affectedRowCount > result.previewRows.length && (
            <div className="mt-2 text-xs text-gray-500 italic">
              {t('manager.modules.ruleComposition.sqlTest.showingRows', { '0': result.previewRows.length, '1': result.affectedRowCount }, `Viser ${result.previewRows.length} av ${result.affectedRowCount} rader`)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Sortable Rule Row Component - REDESIGNED
function SortableRuleRow({
  rule,
  sqlFilters,
  sqlGroups,
  sqlOrders,
  onUpdate,
  onDelete,
  isDeleting,
}: {
  rule: RuleInGroupDto;
  sqlFilters: SqlFilterDto[];
  sqlGroups: SqlGroupDto[];
  sqlOrders: SqlOrderDto[];
  onUpdate: (id: number, request: UpdateRuleInGroupRequest) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  isDeleting: boolean;
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localRule, setLocalRule] = useState(rule);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isTestingSql, setIsTestingSql] = useState(false);
  const [sqlTestResult, setSqlTestResult] = useState<RuleSqlTestResult | null>(null);
  const [sqlTestError, setSqlTestError] = useState<string | null>(null);
  const [sqlFormatMode, setSqlFormatMode] = useState<SqlFormatMode>('compact');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 1,
  };

  useEffect(() => {
    setLocalRule(rule);
    setHasChanges(false);
    setSqlTestResult(null);
    setSqlTestError(null);
  }, [rule]);

  const handleFieldChange = (field: keyof UpdateRuleInGroupRequest, value: any) => {
    setLocalRule(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    // Reset test result when config changes
    setSqlTestResult(null);
    setSqlTestError(null);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onUpdate(rule.id, {
        sqlFilterID: localRule.sqlFilterID,
        sqlGroupID: localRule.sqlGroupID,
        sqlOrderID: localRule.sqlOrderID,
        activeYesNo: localRule.activeYesNo,
        pListBatchTypeNo: localRule.pListBatchTypeNo,
      });
      setHasChanges(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(t('manager.modules.ruleComposition.confirmations.removeRule', { '0': rule.ruleName }, `Er du sikker på at du vil fjerne "${rule.ruleName}" fra denne gruppen?`))) {
      await onDelete(rule.id);
    }
  };

  const handleTestSql = async () => {
    try {
      setIsTestingSql(true);
      setSqlTestResult(null);
      setSqlTestError(null);

      // Call API endpoint to test SQL execution
      const result = await ruleCompositionService.testRuleSql(rule.id);

      if (result.success) {
        setSqlTestResult(result);
      } else {
        setSqlTestError(result.errorMessage || t('manager.modules.ruleComposition.sqlTest.failed', {}, 'SQL-test feilet'));
      }
    } catch (err) {
      setSqlTestError(err instanceof Error ? err.message : t('manager.modules.ruleComposition.sqlTest.failed', {}, 'SQL-test feilet'));
    } finally {
      setIsTestingSql(false);
    }
  };

  // Helper to get display text for SQL references
  const getFilterDisplay = () => {
    if (!localRule.sqlFilterID) return null;
    const filter = sqlFilters.find(f => f.sqlFilterID === localRule.sqlFilterID);
    return filter ? `${filter.lng1} (ID: ${filter.sqlFilterID})` : `ID: ${localRule.sqlFilterID}`;
  };

  const getGroupDisplay = () => {
    if (!localRule.sqlGroupID) return null;
    const group = sqlGroups.find(g => g.sqlGroupID === localRule.sqlGroupID);
    return group ? `${group.lng1} (ID: ${group.sqlGroupID})` : `ID: ${localRule.sqlGroupID}`;
  };

  const getOrderDisplay = () => {
    if (!localRule.sqlOrderID) return null;
    const order = sqlOrders.find(o => o.sqlOrderID === localRule.sqlOrderID);
    return order ? `${order.lng1} (ID: ${order.sqlOrderID})` : `ID: ${localRule.sqlOrderID}`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        border rounded-lg transition-all
        ${isDragging ? 'shadow-xl ring-2 ring-blue-500 opacity-90' : 'shadow-sm'}
        ${!localRule.activeYesNo ? 'bg-gray-100 opacity-75' : 'bg-white'}
      `}
    >
      {/* Main Row - REDESIGNED with inline SQL info */}
      <div className="p-4">
        {/* Top: Rule name + controls */}
        <div className="flex items-start gap-3 mb-3">
          <button
            {...attributes}
            {...listeners}
            className="p-1.5 hover:bg-gray-100 rounded cursor-grab active:cursor-grabbing mt-0.5"
            title={t('manager.modules.ruleComposition.tooltips.dragToReorder', {}, 'Dra for å endre rekkefølge')}
          >
            <GripVertical className="w-5 h-5 text-gray-400" />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 text-base truncate">
                {rule.ruleName}
              </h3>
              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                #{rule.execOrder}
              </span>
            </div>
            {rule.ruleNameEn && (
              <p className="text-sm text-gray-500 truncate">{rule.ruleNameEn}</p>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => handleFieldChange('activeYesNo', !localRule.activeYesNo)}
              className={`p-1.5 rounded transition-colors ${
                localRule.activeYesNo
                  ? 'text-green-600 hover:bg-green-50'
                  : 'text-gray-400 hover:bg-gray-100'
              }`}
              title={localRule.activeYesNo
                ? t('manager.modules.ruleComposition.tooltips.activeClickToDeactivate', {}, 'Aktiv - klikk for å deaktivere')
                : t('manager.modules.ruleComposition.tooltips.inactiveClickToActivate', {}, 'Inaktiv - klikk for å aktivere')}
            >
              {localRule.activeYesNo ? (
                <ToggleRight className="w-6 h-6" />
              ) : (
                <ToggleLeft className="w-6 h-6" />
              )}
            </button>

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title={isExpanded
                ? t('manager.modules.ruleComposition.tooltips.hideDetails', {}, 'Skjul detaljer')
                : t('manager.modules.ruleComposition.tooltips.showDetails', {}, 'Vis detaljer')}
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              )}
            </button>

            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-all"
                title={t('manager.modules.ruleComposition.tooltips.saveChanges', {}, 'Lagre endringer')}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
              </button>
            )}

            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
              title={t('manager.modules.ruleComposition.tooltips.removeFromGroup', {}, 'Fjern fra gruppe')}
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* SQL Configuration Display - INLINE, ALWAYS VISIBLE */}
        <div className="grid grid-cols-3 gap-3 pl-10">
          {/* Filter */}
          <div className="flex items-start gap-2">
            <Filter className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                {t('manager.modules.ruleComposition.sqlConfig.filter', {}, 'Filter')}
              </div>
              <div className="text-sm text-gray-900 font-medium">
                {getFilterDisplay() || (
                  <span className="text-gray-400 italic">{t('manager.modules.ruleComposition.sqlConfig.noFilter', {}, 'Ingen filter')}</span>
                )}
              </div>
            </div>
          </div>

          {/* Grouping */}
          <div className="flex items-start gap-2">
            <Group className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                {t('manager.modules.ruleComposition.sqlConfig.grouping', {}, 'Gruppering')}
              </div>
              <div className="text-sm text-gray-900 font-medium">
                {getGroupDisplay() || (
                  <span className="text-gray-400 italic">{t('manager.modules.ruleComposition.sqlConfig.noGrouping', {}, 'Ingen gruppering')}</span>
                )}
              </div>
            </div>
          </div>

          {/* Sorting */}
          <div className="flex items-start gap-2">
            <ArrowUpDown className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                {t('manager.modules.ruleComposition.sqlConfig.sorting', {}, 'Sortering')}
              </div>
              <div className="text-sm text-gray-900 font-medium">
                {getOrderDisplay() || (
                  <span className="text-gray-400 italic">{t('manager.modules.ruleComposition.sqlConfig.noSorting', {}, 'Ingen sortering')}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SQL Test Button - Prominent placement */}
        {rule.ruleSqlExpression && (
          <div className="mt-3 pl-10">
            <button
              onClick={handleTestSql}
              disabled={isTestingSql}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 font-medium"
            >
              <PlayCircle className="w-4 h-4" />
              {isTestingSql
                ? t('manager.modules.ruleComposition.sqlTest.testing', {}, 'Tester SQL...')
                : t('manager.modules.ruleComposition.sqlTest.testButton', {}, 'Test SQL-uttrykk')}
            </button>
          </div>
        )}

        {/* SQL Test Result with Preview */}
        {(sqlTestResult !== null || sqlTestError !== null || isTestingSql) && (
          <div className="mt-3 pl-10">
            <SqlTestResultDisplay
              isLoading={isTestingSql}
              result={sqlTestResult}
              error={sqlTestError}
            />
          </div>
        )}
      </div>

      {/* Expanded Details - Configuration Controls */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t bg-gray-50">
          <div className="pt-4 space-y-4">
            {/* SQL Expression Display */}
            {rule.ruleSqlExpression && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    <Code className="w-4 h-4" />
                    {t('manager.modules.ruleComposition.sqlConfig.sqlExpression', {}, 'SQL-uttrykk')}
                  </label>
                  <div className="flex items-center border border-gray-300 rounded overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setSqlFormatMode('compact')}
                      className={`flex items-center gap-1 px-2 py-1 text-xs border-r border-gray-300 ${sqlFormatMode === 'compact' ? 'bg-gray-200 text-gray-900 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                      title={t('manager.modules.ruleComposition.sqlConfig.compactTooltip', {}, 'Kompakt formatering - mindre innrykk')}
                    >
                      <AlignJustify className="w-3 h-3" />
                      {t('manager.modules.ruleComposition.sqlConfig.compact', {}, 'Kompakt')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSqlFormatMode('standard')}
                      className={`flex items-center gap-1 px-2 py-1 text-xs ${sqlFormatMode === 'standard' ? 'bg-gray-200 text-gray-900 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                      title={t('manager.modules.ruleComposition.sqlConfig.standardTooltip', {}, 'Standard formatering - fullt innrykk')}
                    >
                      <AlignLeft className="w-3 h-3" />
                      {t('manager.modules.ruleComposition.sqlConfig.standard', {}, 'Standard')}
                    </button>
                  </div>
                </div>
                <pre className="p-3 bg-gray-900 rounded-lg text-xs font-mono text-green-400 max-h-64 overflow-auto whitespace-pre-wrap">
                  {formatSql(rule.ruleSqlExpression, sqlFormatMode)}
                </pre>
              </div>
            )}

            {/* Configuration Selectors */}
            <div className="grid grid-cols-3 gap-4">
              {/* SQL Filter Selector */}
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  <Filter className="w-4 h-4" />
                  {t('manager.modules.ruleComposition.sqlConfig.selectFilter', {}, 'Velg SQL Filter')}
                </label>
                <select
                  value={localRule.sqlFilterID || 0}
                  onChange={(e) => handleFieldChange('sqlFilterID', parseInt(e.target.value) || null)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value={0}>{t('manager.modules.ruleComposition.sqlConfig.noFilter', {}, 'Ingen filter')}</option>
                  {sqlFilters.map((filter) => (
                    <option key={filter.sqlFilterID} value={filter.sqlFilterID}>
                      {filter.lng1} (ID: {filter.sqlFilterID})
                    </option>
                  ))}
                </select>
                {localRule.sqlFilterSyntax && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs font-mono text-blue-700 overflow-auto max-h-20">
                    {localRule.sqlFilterSyntax}
                  </div>
                )}
              </div>

              {/* SQL Group Selector */}
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  <Group className="w-4 h-4" />
                  {t('manager.modules.ruleComposition.sqlConfig.selectGrouping', {}, 'Velg SQL Gruppering')}
                </label>
                <select
                  value={localRule.sqlGroupID || 0}
                  onChange={(e) => handleFieldChange('sqlGroupID', parseInt(e.target.value) || null)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value={0}>{t('manager.modules.ruleComposition.sqlConfig.noGrouping', {}, 'Ingen gruppering')}</option>
                  {sqlGroups.map((group) => (
                    <option key={group.sqlGroupID} value={group.sqlGroupID}>
                      {group.lng1} (ID: {group.sqlGroupID})
                    </option>
                  ))}
                </select>
                {localRule.sqlGroupSyntax && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs font-mono text-green-700 overflow-auto max-h-20">
                    {localRule.sqlGroupSyntax}
                  </div>
                )}
              </div>

              {/* SQL Order Selector */}
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  <ArrowUpDown className="w-4 h-4" />
                  {t('manager.modules.ruleComposition.sqlConfig.selectSorting', {}, 'Velg SQL Sortering')}
                </label>
                <select
                  value={localRule.sqlOrderID || 0}
                  onChange={(e) => handleFieldChange('sqlOrderID', parseInt(e.target.value) || null)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value={0}>{t('manager.modules.ruleComposition.sqlConfig.noSorting', {}, 'Ingen sortering')}</option>
                  {sqlOrders.map((order) => (
                    <option key={order.sqlOrderID} value={order.sqlOrderID}>
                      {order.lng1} (ID: {order.sqlOrderID})
                    </option>
                  ))}
                </select>
                {localRule.sqlOrderSyntax && (
                  <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs font-mono text-purple-700 overflow-auto max-h-20">
                    {localRule.sqlOrderSyntax}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RuleGroupDetailModal({
  group,
  onClose,
  onRulesChanged,
}: RuleGroupDetailModalProps) {
  const { t } = useTranslation();
  const [rulesInGroup, setRulesInGroup] = useState<RuleInGroupDto[]>([]);
  const [sqlFilters, setSqlFilters] = useState<SqlFilterDto[]>([]);
  const [sqlGroups, setSqlGroups] = useState<SqlGroupDto[]>([]);
  const [sqlOrders, setSqlOrders] = useState<SqlOrderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rules, filters, groups, orders] = await Promise.all([
        ruleCompositionService.getRulesInGroup(group.ruleGroupID),
        ruleCompositionService.getSqlFilters(),
        ruleCompositionService.getSqlGroups(),
        ruleCompositionService.getSqlOrders(),
      ]);
      setRulesInGroup(rules);
      setSqlFilters(filters);
      setSqlGroups(groups);
      setSqlOrders(orders);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.ruleComposition.errors.loadFailed', {}, 'Feil ved lasting av data'));
    } finally {
      setLoading(false);
    }
  }, [group.ruleGroupID, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rulesInGroup.findIndex((r) => r.id === active.id);
      const newIndex = rulesInGroup.findIndex((r) => r.id === over.id);

      const newOrder = arrayMove(rulesInGroup, oldIndex, newIndex);
      setRulesInGroup(newOrder);

      // Save new order to backend
      try {
        setIsSavingOrder(true);
        await ruleCompositionService.reorderRules(
          group.ruleGroupID,
          newOrder.map((r) => r.id)
        );
        // Reload to get updated ExecOrder values
        const updatedRules = await ruleCompositionService.getRulesInGroup(group.ruleGroupID);
        setRulesInGroup(updatedRules);
      } catch (err) {
        console.error('Failed to save order:', err);
        // Revert on error
        await loadData();
      } finally {
        setIsSavingOrder(false);
      }
    }
  };

  const handleUpdate = async (id: number, request: UpdateRuleInGroupRequest) => {
    await ruleCompositionService.updateRuleInGroup(id, request);
    // Reload to get fresh data
    const updatedRules = await ruleCompositionService.getRulesInGroup(group.ruleGroupID);
    setRulesInGroup(updatedRules);
  };

  const handleDelete = async (id: number) => {
    try {
      setDeletingId(id);
      await ruleCompositionService.deleteRuleInGroup(id);
      setRulesInGroup((prev) => prev.filter((r) => r.id !== id));
      onRulesChanged();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-500">
          <div className="text-white">
            <h2 className="text-xl font-bold">{group.lng1}</h2>
            {group.lng2 && <p className="text-sm opacity-90 mt-1">{group.lng2}</p>}
          </div>
          <div className="flex items-center gap-3">
            {isSavingOrder && (
              <span className="text-sm text-white flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('manager.modules.ruleComposition.status.savingOrder', {}, 'Lagrer rekkefølge...')}
              </span>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
              title={t('common.buttons.close', {}, 'Lukk')}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">{t('manager.modules.ruleComposition.status.loadingRules', {}, 'Laster regler...')}</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <AlertCircle className="w-16 h-16 text-red-500" />
              <p className="text-red-600 font-medium">{error}</p>
              <button
                onClick={loadData}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:opacity-90 transition-all font-medium"
              >
                {t('manager.modules.ruleComposition.actions.retry', {}, 'Prøv igjen')}
              </button>
            </div>
          ) : rulesInGroup.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <div className="max-w-md mx-auto">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">{t('manager.modules.ruleComposition.emptyState.noRulesInGroup', {}, 'Ingen regler i denne gruppen')}</p>
                <p className="text-sm">
                  {t('manager.modules.ruleComposition.emptyState.dragHint', {}, 'Dra regler fra hovedsiden og slipp dem på denne gruppen for å legge til.')}
                </p>
              </div>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={rulesInGroup.map((r) => r.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {rulesInGroup.map((rule) => (
                    <SortableRuleRow
                      key={rule.id}
                      rule={rule}
                      sqlFilters={sqlFilters}
                      sqlGroups={sqlGroups}
                      sqlOrders={sqlOrders}
                      onUpdate={handleUpdate}
                      onDelete={handleDelete}
                      isDeleting={deletingId === rule.id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-white flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600">
              {rulesInGroup.length === 1
                ? t('manager.modules.ruleComposition.footer.rulesInGroupOne', { '0': rulesInGroup.length }, `${rulesInGroup.length} regel i gruppen`)
                : t('manager.modules.ruleComposition.footer.rulesInGroupMany', { '0': rulesInGroup.length }, `${rulesInGroup.length} regler i gruppen`)}
            </span>
            {rulesInGroup.length > 0 && (
              <span className="text-xs text-gray-400">
                {t('manager.modules.ruleComposition.tooltips.dragToReorder', {}, 'Dra for å endre rekkefølge')}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:opacity-90 transition-all font-medium"
          >
            {t('common.buttons.close', {}, 'Lukk')}
          </button>
        </div>
      </div>
    </div>
  );
}
